import React from 'react';
import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap } from 'lucide-react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Lazy load pages for better performance
const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const FAQs = React.lazy(() => import('./pages/FAQs'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Booking = React.lazy(() => import('./pages/Booking'));
const CaseStudies = React.lazy(() => import('./pages/CaseStudies'));
const HandOverTheKeys = React.lazy(() => import('./pages/HandOverTheKeys'));
const KeepTheEngineRunning = React.lazy(() => import('./pages/KeepTheEngineRunning'));
const DriveTheFuture = React.lazy(() => import('./pages/DriveTheFuture'));
const CaseStudyFasterGrowth = React.lazy(() => import('./pages/CaseStudyFasterGrowth'));
const CaseStudyIncreasedEfficiency = React.lazy(() => import('./pages/CaseStudyIncreasedEfficiency'));
const CaseStudyTimeSaving = React.lazy(() => import('./pages/CaseStudyTimeSaving'));
const CaseStudyCostOptimization = React.lazy(() => import('./pages/CaseStudyCostOptimization'));
const ChatbotDevelopment = React.lazy(() => import('./pages/ChatbotDevelopment'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const AskQuestion = React.lazy(() => import('./pages/AskQuestion'));
const AutomatedOutreach = React.lazy(() => import('./pages/AutomatedOutreach'));
const FollowUpSystem = React.lazy(() => import('./pages/FollowUpSystem'));
const WebsiteOptimization = React.lazy(() => import('./pages/WebsiteOptimization'));
const AppDevelopment = React.lazy(() => import('./pages/AppDevelopment'));
const CustomAutomation = React.lazy(() => import('./pages/CustomAutomation'));

// Lazy load non-critical components
const CookieConsent = lazy(() => import('./components/ui/cookie-consent').then(module => ({ default: module.CookieConsent })));
import ScrollToTop from './components/ScrollToTop';

function App() {
  const handleCookieAccept = (preferences: boolean[]) => {
    console.log('🍪 Cookie preferences accepted:', {
      essential: preferences[0] ? '✅ Enabled' : '❌ Disabled',
      analytics: preferences[1] ? '✅ Enabled' : '❌ Disabled',
      marketing: preferences[2] ? '✅ Enabled' : '❌ Disabled',
    });
    
    // Enable/disable Google Analytics based on preferences
    if (preferences[1] && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    } else if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  const handleCookieDecline = () => {
    console.log('❌ User declined all non-essential cookies');
    
    // Disable Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen circuit-bg flex flex-col">
        <Navbar />
        <main className="flex-grow">
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-dark-blue">
              <div className="text-center">
                <motion.div
                  className="relative mx-auto mb-6"
                  initial={{ scale: 1 }}
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{
                    duration: 0.8,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0.5 }}
                    animate={{ 
                      opacity: [0.5, 1, 0.7, 1, 0.5],
                      filter: [
                        "drop-shadow(0 0 0px #00f2ff)",
                        "drop-shadow(0 0 20px #00f2ff)",
                        "drop-shadow(0 0 10px #00f2ff)",
                        "drop-shadow(0 0 25px #00f2ff)",
                        "drop-shadow(0 0 0px #00f2ff)"
                      ]
                    }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <Zap className="w-16 h-16 text-electric-blue" />
                  </motion.div>
                  
                  {/* Lightning sparks effect */}
                  <motion.div
                    className="absolute -top-2 -right-2"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ 
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0]
                    }}
                    transition={{
                      duration: 0.6,
                      repeat: Infinity,
                      delay: 0.3,
                      ease: "easeOut"
                    }}
                  >
                    <Zap className="w-4 h-4 text-electric-blue/70" />
                  </motion.div>
                  
                  <motion.div
                    className="absolute -bottom-2 -left-2"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ 
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0]
                    }}
                    transition={{
                      duration: 0.6,
                      repeat: Infinity,
                      delay: 0.7,
                      ease: "easeOut"
                    }}
                  >
                    <Zap className="w-4 h-4 text-electric-blue/70" />
                  </motion.div>
                </motion.div>
                
                <motion.p 
                  className="text-text-light/80 font-mono uppercase tracking-wider"
                  initial={{ opacity: 0.7 }}
                  animate={{ 
                    opacity: [0.7, 1, 0.7]
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  Powering up...
                </motion.p>
              </div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/faqs" element={<FAQs />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/book" element={<Booking />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/hand-over-the-keys" element={<HandOverTheKeys />} />
              <Route path="/keep-the-engine-running" element={<KeepTheEngineRunning />} />
              <Route path="/drive-the-future" element={<DriveTheFuture />} />
              <Route path="/case-study/faster-growth" element={<CaseStudyFasterGrowth />} />
              <Route path="/case-study/increased-efficiency" element={<CaseStudyIncreasedEfficiency />} />
              <Route path="/case-study/time-saving" element={<CaseStudyTimeSaving />} />
              <Route path="/case-study/cost-optimization" element={<CaseStudyCostOptimization />} />
              <Route path="/chatbot-development" element={<ChatbotDevelopment />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/ask-question" element={<AskQuestion />} />
              <Route path="/automated-outreach" element={<AutomatedOutreach />} />
              <Route path="/follow-up-system" element={<FollowUpSystem />} />
              <Route path="/website-optimization" element={<WebsiteOptimization />} />
              <Route path="/app-development" element={<AppDevelopment />} />
              <Route path="/custom-automation" element={<CustomAutomation />} />
              {/* 404 Catch-all route - redirect to /404 for proper HTTP status */}
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <Suspense fallback={null}>
          <CookieConsent
            cookiePolicyUrl="/privacy-policy"
            onAccept={handleCookieAccept}
            onDecline={handleCookieDecline}
          />
        </Suspense>
      </div>
    </Router>
  );
}

export default App;